import React, { Fragment, useEffect, useState } from 'react';
import {useGetAndStartCaseDatabaseStatus, useGetCaseDatabaseVersion, isCaseDbReady} from '../../api/database_status.js';
import DatabaseRestartingPage from './DatabaseRestartingPage.js';
import CaseDatabaseUpdatingPage from './CaseDatabaseUpdatingPage.js';

// The version check normally answers in about a second; only show the waiting
// card when it takes longer, so a normal case open doesn't flash it.
const VERSION_WAIT_CARD_DELAY_MS = 1500;

function useDelayedFlag(active, delayMs) {
    const [shown, setShown] = useState(false);
    useEffect(() => {
        if (!active) {
            setShown(false);
            return undefined;
        }
        const timer = setTimeout(() => setShown(true), delayMs);
        return () => clearTimeout(timer);
    }, [active, delayMs]);
    return shown;
}

export default function CaseDatabaseCheck(props) {

    const dbStatus = useGetAndStartCaseDatabaseStatus();
    const dbVersion = useGetCaseDatabaseVersion(isCaseDbReady(dbStatus.data?.status));
    // Right after a cold start the status ping can succeed before the database
    // answers queries; the version request then retries (and can time out at the
    // gateway) for a minute or more. Show a waiting card rather than a blank page.
    const versionPending = isCaseDbReady(dbStatus.data?.status) && !dbVersion.data;
    const showVersionWait = useDelayedFlag(versionPending, VERSION_WAIT_CARD_DELAY_MS);

    if (dbStatus.isLoading || !dbStatus.data){
        return <></>;
    }

    if (dbStatus.data.status === "UNAUTHORIZED") {
        return (<Fragment>You are not currently working in a case.</Fragment>);
    }

    if (!isCaseDbReady(dbStatus.data.status)) {
        return <DatabaseRestartingPage isCaseDatabase={true}/>;
    }

    if (dbVersion.isLoading || !dbVersion.data){
        return showVersionWait ? <CaseDatabaseUpdatingPage checking /> : <></>;
    }

    // A stale version is migrated automatically: the version check itself
    // starts the migration server-side, and the version hook keeps polling
    // until the case is current, at which point the case renders.
    if (!dbVersion.data.is_current_version){
        return (
            <CaseDatabaseUpdatingPage
                fromVersion={dbVersion.data.version}
                toVersion={dbVersion.data.target_version}
                failed={dbVersion.data.migration_status === 'failed'}
            />
        );
    }

    return <Fragment>{props.children}</Fragment>;
}
